<template>
    <div>
  
      <!-- Page Header (screen only) -->
      <div class="page-header no-print">
        <div>
          <h5 class="page-title">Purchase Receipt</h5>
          <p class="page-sub">
            <span class="batch-pill">{{ purchase?.batch_code }}</span>
          </p>
        </div>
        <div class="page-actions">
          <router-link :to="$url('/all-purchases')" class="btn-outline">
            <font-awesome-icon icon="arrow-left" /> Back
          </router-link>
          <PrintButtonHeader
            :title="title + purchase?.batch_code"
            :urlA4="urlA4" :url58mm="url58mm" :url80mm="url80mm"
            :currentUrl="currentUrl" :data="data" :type="type"
          />
          <button class="btn-primary-action no-print" onclick="window.print()">
            <font-awesome-icon icon="print" /> Print
          </button>
        </div>
      </div>
  
      <!-- Skeleton -->
      <div v-if="loading" class="receipt-skeleton no-print">
        <div class="skeleton skeleton--hero"></div>
        <div class="skeleton skeleton--line mt-2" v-for="n in 6" :key="n"></div>
      </div>
  
      <!-- Receipt -->
      <div v-else class="receipt-wrap">
        <div class="receipt" id="printMe">
  
          <!-- Header -->
          <div class="receipt-header">
            <p class="receipt-company">{{ company?.name }}</p>
            <p class="receipt-detail" v-if="purchase?.store?.address">{{ purchase.store.address }}</p>
            <p class="receipt-detail" v-if="company?.phone_number">{{ company.phone_number }}</p>
            <p class="receipt-detail" v-if="company?.email">{{ company.email }}</p>
          </div>
  
          <div class="receipt-dots"></div>
  
          <!-- Meta -->
          <div class="receipt-meta">
            <div class="receipt-meta-row">
              <span>Date</span>
              <span>{{ dateTime(purchase?.date) }}, {{ timeConvert(purchase?.time) }}</span>
            </div>
            <div class="receipt-meta-row">
              <span>Purchase No</span>
              <span class="mono">{{ purchase?.batch_code }}</span>
            </div>
            <div class="receipt-meta-row">
              <span>Store</span>
              <span>{{ purchase?.store?.store_name }}</span>
            </div>
            <div class="receipt-meta-row" v-if="purchase?.supplier?.supplier_name">
              <span>Supplier</span>
              <span>{{ purchase.supplier.supplier_name }}</span>
            </div>
            <div class="receipt-meta-row">
              <span>Purchased By</span>
              <span>{{ purchase?.user?.fullname }}</span>
            </div>
          </div>
  
          <div class="receipt-dots"></div>
          <div class="receipt-line"></div>
  
          <!-- Items -->
          <div class="receipt-items">
            <div v-for="item in purchase_array" :key="item.product_name" class="receipt-item">
              <p class="receipt-item-name">{{ item?.product_name }}</p>
  
              <div v-if="item?.variant" class="receipt-item-row">
                <span>
                  {{ item.quantity }} {{ item.variant?.variant_name }} × {{ $filters.formatMoney(item?.cost_price) }}
                </span>
                <span class="fw-bold">{{ $filters.formatMoney(item?.total, company?.currency?.symbol) }}</span>
              </div>
              <div v-else class="receipt-item-row">
                <span>
                  {{ item?.quantity }} {{ item?.variant_name }} × {{ $filters.formatMoney(item?.cost_price) }}
                </span>
                <span class="fw-bold">{{ $filters.formatMoney(item?.total, company?.currency?.symbol) }}</span>
              </div>
  
              <div class="receipt-line mt-1"></div>
            </div>
          </div>
  
          <div class="receipt-dots"></div>
  
          <!-- Totals -->
          <div class="receipt-totals">
            <div class="receipt-line"></div>
            <div class="receipt-totals-row receipt-totals-row--grand">
              <span>Total Amount</span>
              <span>{{ $filters.formatMoney(purchase?.total_amount, company?.currency?.symbol) }}</span>
            </div>
          </div>
  
          <div class="receipt-dots"></div>
  
          <!-- Payment -->
          <div class="receipt-payment">
            <div class="receipt-totals-row">
              <span>Payment</span>
              <span class="text-capitalize">{{ purchase?.payment_type }}</span>
            </div>
            <div class="receipt-totals-row">
              <span>Amount Paid</span>
              <span>{{ $filters.formatMoney(purchase?.paid_amount ?? 0, company?.currency?.symbol) }}</span>
            </div>
            <div class="receipt-totals-row"
              v-if="purchase?.payment_type === 'cash' || purchase?.payment_type === 'card'">
              <span>Change</span>
              <span>{{ $filters.formatMoney(purchase?.change, company?.currency?.symbol) }}</span>
            </div>
            <div class="receipt-totals-row">
              <span>Balance</span>
              <span :class="parseFloat(purchase?.balance ?? 0) > 0 ? 'text-neg' : ''">
                {{ $filters.formatMoney(purchase?.balance ?? 0, company?.currency?.symbol) }}
              </span>
            </div>
          </div>
  
          <div class="receipt-dots"></div>
  
          <!-- Footer -->
          <div class="receipt-footer">
            <p><span v-html="poweredBy()"></span></p>
            <!-- <p class="receipt-footer-url">www.mapiibs.co.uk</p> -->
          </div>
  
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  import { reusableMixin } from '../../../../lib/reusableMixin';
  
  export default {
    mixins: [reusableMixin],
  
    props: {
      title:           { type: String,  default: '' },
      urlA4:           { type: String,  default: '' },
      url58mm:         { type: String,  default: '' },
      url80mm:         { type: String,  default: '' },
      currentUrl:      { type: String,  default: '' },
      type:            { type: String,  default: '' },
      data:            { type: Object,  default: () => ({}) },
      purchase_items:  { type: Array,   default: () => [] },
      purchase_array:  { type: Array,   default: () => [] },
      purchase:        { type: Object,  default: () => ({}) },
      creditor:        { type: Object,  default: () => ({}) },
      creditor_credits:{ type: Object,  default: () => ({}) },
      loading:         { type: Boolean, default: false },
    },
  
    data() {
      return {
        toast: useToast(),
        store: useStore(),
      };
    },
  
    created() { this.getCompany(); },
  };
  </script>
  
  <style scoped>
  /* ── Screen: Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .page-sub     { margin:0; }
  .page-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  .batch-pill   { display:inline-block; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; border-radius:20px; padding:2px 12px; font-size:13px; font-weight:600; }
  
  /* ── Skeleton ── */
  .receipt-skeleton { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; max-width:375px; margin:0 auto; }
  .skeleton         { border-radius:6px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton--hero { height:72px; }
  .skeleton--line { height:18px; }
  .mt-2 { margin-top:8px; }
  
  /* ── Receipt Wrap ── */
  .receipt-wrap { display:flex; justify-content:center; padding-bottom:32px; }
  
  /* ── Receipt Shell ── */
  .receipt {
    background:#fff;
    border:1px solid #e4eaf2;
    border-radius:12px;
    box-shadow:0 4px 24px rgba(0,0,0,.06);
    padding:24px 20px;
    width:375px;
    font-family:'Inter', sans-serif;
    font-size:13.5px;
    color:#35413B;
    line-height:1.7;
  }
  
  /* ── Header ── */
  .receipt-header   { text-align:center; margin-bottom:12px; }
  .receipt-company  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
  .receipt-detail   { font-size:12.5px; color:#4a5e78; margin:0; }
  
  /* ── Dots / Line ── */
  .receipt-dots { border:none; border-top:2px dashed #dde5f0; margin:12px 0; }
  .receipt-line { height:1px; background:#e4eaf2; margin:6px 0; }
  
  /* ── Meta ── */
  .receipt-meta     { display:flex; flex-direction:column; gap:2px; }
  .receipt-meta-row { display:flex; justify-content:space-between; font-size:13px; color:#4a5e78; }
  .receipt-meta-row span:last-child { font-weight:600; color:#1a2b45; text-align:right; }
  .mono { font-family:monospace; font-size:12.5px; }
  
  /* ── Items ── */
  .receipt-items    { display:flex; flex-direction:column; gap:4px; }
  .receipt-item-name { font-weight:600; color:#1a2b45; margin:0 0 2px; }
  .receipt-item-row  { display:flex; justify-content:space-between; font-size:13px; color:#4a5e78; }
  .receipt-item-row .fw-bold { color:#1a2b45; }
  .mt-1 { margin-top:4px; }
  
  /* ── Totals ── */
  .receipt-totals       { display:flex; flex-direction:column; gap:3px; }
  .receipt-totals-row   { display:flex; justify-content:space-between; font-size:13px; color:#4a5e78; }
  .receipt-totals-row--grand { font-size:15px; font-weight:800; color:#1a2b45; margin-top:4px; }
  .receipt-payment      { display:flex; flex-direction:column; gap:3px; }
  .text-capitalize      { text-transform:capitalize; }
  .text-neg             { color:#e03535; font-weight:600; }
  
  /* ── Footer ── */
  .receipt-footer     { text-align:center; font-size:12px; color:#aab8cc; }
  .receipt-footer-url { font-size:11px; color:#c5d0dc; margin:0; }
  
  /* ── Buttons ── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover { background:#003b8e; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; text-decoration:none; }
  .btn-outline:hover { background:#f4f7fb; color:#1a2b45; }
  
  /* ── Print ── */
  @media print {
    .no-print   { display:none !important; }
    .receipt-wrap { padding:0; }
    .receipt    { border:none; border-radius:0; box-shadow:none; width:80mm; padding:8px; }
    body        { background:#fff; }
  }
  
  /* ── Responsive ── */
  @media(max-width:576px) {
    .receipt    { width:100%; }
    .page-header,.page-actions { flex-direction:column; align-items:stretch; }
    .btn-outline,.btn-primary-action { width:100%; justify-content:center; }
  }
  </style>